import { useEffect, useState } from "react";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Loader2, Building2, Users, FileText, Globe, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

function buildSignupsCsv(rows) {
  const headers = ["Name", "Email", "Role", "Organisation", "Signed up"];
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(",")];
  for (const r of rows) lines.push([r.name, r.email, r.role, r.organisation_name, r.created_at].map(esc).join(","));
  return lines.join("\n");
}

export default function PlatformOverview() {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const exportSignups = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/platform/signups");
      const blob = new Blob([buildSignupsCsv(data)], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sign-off-signups-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [overview, usersRes] = await Promise.all([
          api.get("/platform/overview"),
          api.get("/platform/users"),
        ]);
        setData(overview.data);
        setUsers(usersRes.data);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Layout>
      <div className="mb-8 animate-fade-up flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2 flex items-center gap-1.5">
            <Globe className="h-3.5 w-3.5" /> Super-admin
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl text-forest-900 tracking-tight">Platform overview</h1>
          <p className="text-sm text-ink-soft mt-2 max-w-lg">
            Aggregate activity across every organisation. Individual expense details remain private to each charity.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={exportSignups}
          disabled={exporting || loading}
          data-testid="export-signups-button"
          className="shrink-0 h-10 border-cream-200 text-forest-800 hover:bg-cream-100"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" /> Export signups</>}
        </Button>
      </div>

      {loading ? (
        <div className="p-12 flex items-center justify-center text-ink-soft"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…</div>
      ) : error ? (
        <div data-testid="platform-error" className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-4 py-3">{error}</div>
      ) : (
        <div className="space-y-8">
          {/* Totals */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="platform-totals">
            <Totals icon={Building2} label="Organisations" value={data.totals.organisations} testid="total-orgs" />
            <Totals icon={Users} label="Users" value={data.totals.users} testid="total-users" />
            <Totals icon={FileText} label="Expenses" value={data.totals.expenses} testid="total-expenses" />
          </div>
          <div className="text-xs text-ink-soft -mt-4" data-testid="expenses-breakdown">
            Expenses: <strong className="text-amber-700">{data.totals.expenses_by_status.awaiting_approval}</strong> awaiting ·{" "}
            <strong className="text-emerald-700">{data.totals.expenses_by_status.approved}</strong> approved ·{" "}
            <strong className="text-rose-700">{data.totals.expenses_by_status.rejected}</strong> rejected
          </div>

          {/* Organisations */}
          <section>
            <h2 className="font-heading text-lg text-forest-900 mb-3">Organisations ({data.organisations.length})</h2>
            <div className="bg-white border border-cream-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="orgs-table">
                <thead>
                  <tr className="bg-cream-100 text-ink-soft text-[11px] uppercase tracking-wider">
                    <th className="text-left font-semibold px-4 py-2.5">Organisation</th>
                    <th className="text-right font-semibold px-4 py-2.5">Members</th>
                    <th className="text-right font-semibold px-4 py-2.5">Expenses</th>
                    <th className="text-right font-semibold px-4 py-2.5">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {data.organisations.map((o) => (
                    <tr key={o.id} className="border-t border-cream-200" data-testid={`org-row-${o.id}`}>
                      <td className="px-4 py-2.5 font-medium text-forest-900">{o.name}</td>
                      <td className="px-4 py-2.5 text-right text-ink">{o.members}</td>
                      <td className="px-4 py-2.5 text-right text-ink">{o.expenses}</td>
                      <td className="px-4 py-2.5 text-right text-ink-soft text-xs">{formatDateTime(o.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Recent signups */}
          <section>
            <h2 className="font-heading text-lg text-forest-900 mb-3">Recent signups</h2>
            <div className="bg-white border border-cream-200 rounded-xl divide-y divide-cream-200" data-testid="recent-signups">
              {data.recent_signups.map((u, i) => (
                <div key={u.email || i} className="px-4 py-3 flex items-center justify-between gap-2" data-testid={`signup-${u.email}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-forest-900 truncate">{u.name || u.email}</div>
                    <div className="text-xs text-ink-soft truncate">{u.email} · {u.organisation_name}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md font-semibold ${u.role === "admin" ? "bg-forest-50 text-forest-800" : "bg-cream-100 text-ink-soft"}`}>{u.role}</span>
                    <div className="text-[11px] text-ink-mute mt-1">{formatDateTime(u.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* All users */}
          <section>
            <h2 className="font-heading text-lg text-forest-900 mb-3">All users ({users.length})</h2>
            <div className="bg-white border border-cream-200 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead>
                  <tr className="bg-cream-100 text-ink-soft text-[11px] uppercase tracking-wider">
                    <th className="text-left font-semibold px-4 py-2.5">Name</th>
                    <th className="text-left font-semibold px-4 py-2.5">Email</th>
                    <th className="text-left font-semibold px-4 py-2.5">Role</th>
                    <th className="text-left font-semibold px-4 py-2.5">Organisation</th>
                    <th className="text-right font-semibold px-4 py-2.5">Signed up</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={u.email || i} className="border-t border-cream-200" data-testid={`user-row-${u.email}`}>
                      <td className="px-4 py-2.5 font-medium text-forest-900">{u.name || "—"}</td>
                      <td className="px-4 py-2.5 text-ink">{u.email}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md font-semibold ${u.role === "admin" ? "bg-forest-50 text-forest-800" : "bg-cream-100 text-ink-soft"}`}>{u.role}</span>
                      </td>
                      <td className="px-4 py-2.5 text-ink">{u.organisation_name}</td>
                      <td className="px-4 py-2.5 text-right text-ink-soft text-xs">{formatDateTime(u.created_at)}</td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-ink-soft text-sm">No users yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </Layout>
  );
}

function Totals({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-cream-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-ink-soft mb-2">
        <Icon className="h-4 w-4 text-forest-700" />
        <span className="text-[10px] uppercase tracking-[0.16em] font-semibold">{label}</span>
      </div>
      <div className="font-heading text-4xl text-forest-900">{value}</div>
    </div>
  );
}
